import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  list,
  remove,
} from "../../../services/attractionService";
import Dialog from "../../dialog/Dialog";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import Toster from "../../common/Toster";
import Pagination from "../../common/Pagination";
import {
  AdminTable,
  AdminTableHead,
  AdminTableTh,
  AdminTableBody,
  AdminTableRow,
  AdminTableCell,
  AdminTableFooter,
  TableSearchInput,
  TableEmptyRow,
  EditActionButton,
  DeleteActionButton,
  TableActionsCell,
} from "../../common/adminTable";
import { buildPaginationMeta } from "../../../utils/clientPagination";
import { formatListingDateTime } from "../../../utils/formatListingDate";
import {
  AdminPageLayout,
  AdminPageHeader,
  AdminPageCard,
  AdminPageCardToolbar,
} from "../../common/AdminPageLayout";
import { SUPPLIER_PAGE_META } from "../../common/supplierPageMeta";
import { getTableEmptyProps } from "../../common/supplierTableEmpty";
import {
  SupplierAddButton,
  SupplierFilterToolbar,
  SupplierFilterField,
  SupplierPageLoading,
} from "../../common/supplierPageUi";

function attractionApprovalCode(isApproved) {
  if (isApproved === true || isApproved === 1 || isApproved === "1") return 1;
  if (isApproved === 2 || isApproved === "2") return 2;
  return 0;
}

const formatTimings = (attraction) => {
  const convertTo12Hour = (time) => {
    if (!time) return "";
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  if (!attraction.start_time || !attraction.end_time) return "N/A";
  return `${convertTo12Hour(attraction.start_time)} - ${convertTo12Hour(attraction.end_time)}`;
};

const Attractions = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAttraction, setSelectedAttraction] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isActiveLoading, setIsActiveLoading] = useState(false);

  const queryClient = useQueryClient();
  const pageMeta = SUPPLIER_PAGE_META.attractions;

  const { data, isLoading, error } = useQuery({
    queryKey: ["attractions", currentPage, searchTerm],
    queryFn: () => list(currentPage, searchTerm),
    keepPreviousData: true,
  });

  const showSuccessMessage = (message) => {
    setSuccessMessage(message);
    setTimeout(() => {
      setSuccessMessage("");
    }, 3000);
  };

  const showErrorMessage = (message) => {
    setErrorMessage(message);
    setTimeout(() => {
      setErrorMessage("");
    }, 3000);
  };

  const deleteMutation = useMutation({
    mutationFn: remove,
    onSuccess: (success) => {
      queryClient.invalidateQueries(["attractions"]);
      setDeleteDialogOpen(false);
      setSelectedAttraction(null);
      setIsActiveLoading(false);
      showSuccessMessage(
        success.response?.data?.message || "Attraction deleted successfully"
      );
    },
    onError: (err) => {
      setIsActiveLoading(false);
      showErrorMessage(err.response?.data?.message || "Something went wrong");
    },
  });

  const handleAddClick = () => {
    navigate("/attractions/new", { state: { returnTo: "/Attractions" } });
  };

  const handleEditClick = (id) => {
    navigate(`/attractions/${id}/edit`, { state: { returnTo: "/Attractions" } });
  };

  const handleDeleteClick = (attraction) => {
    setSelectedAttraction(attraction);
    setDeleteDialogOpen(true);
  };

  const handleDeleteAttraction = () => {
    setIsActiveLoading(true);
    deleteMutation.mutate(selectedAttraction.id);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const attractions = Array.isArray(data?.data?.data) ? data.data.data : [];
  const current_page = data?.data?.current_page ?? 1;
  const last_page = data?.data?.last_page ?? 1;
  const total = data?.data?.total ?? 0;
  const per_page = data?.data?.per_page ?? 10;

  const pagination = buildPaginationMeta({
    currentPage: current_page,
    lastPage: last_page,
    perPage: per_page,
    total,
  });

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const renderStatus = (attraction) => {
    const approval = attractionApprovalCode(attraction.is_approved);
    const label =
      approval === 1
        ? "Approved"
        : approval === 2
        ? "Rejected"
        : attraction.submitted_at
        ? "Pending Approval"
        : "Draft";
    const className =
      approval === 1
        ? "bg-green-100 text-green-800"
        : approval === 2
        ? "bg-red-100 text-red-800"
        : attraction.submitted_at
        ? "bg-yellow-100 text-yellow-800"
        : "bg-gray-100 text-gray-700";

    return (
      <span className={`px-2 py-1 rounded-full text-[10px] ${className}`}>
        {label}
      </span>
    );
  };

  if (isLoading) {
    return (
      <AdminPageLayout>
        <AdminPageHeader title={pageMeta.title} subtitle={pageMeta.subtitle} />
        <AdminPageCard>
          <SupplierPageLoading message="Loading attractions..." />
        </AdminPageCard>
      </AdminPageLayout>
    );
  }

  if (error) {
    return (
      <AdminPageLayout>
        <AdminPageHeader title={pageMeta.title} subtitle={pageMeta.subtitle} />
        <AdminPageCard>
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <i className="fi fi-rr-exclamation text-red-500 text-4xl mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Error loading attractions
              </h3>
              <p className="text-gray-600">{error.message}</p>
            </div>
          </div>
        </AdminPageCard>
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout>
      <AdminPageHeader
        title={pageMeta.title}
        subtitle={pageMeta.subtitle}
        actions={
          <SupplierAddButton onClick={handleAddClick}>Add Attraction</SupplierAddButton>
        }
      />

      <AdminPageCard>
        <AdminPageCardToolbar>
          <SupplierFilterToolbar>
            <SupplierFilterField label="Search">
              <TableSearchInput
                placeholder="Search attractions..."
                className="mt-1 w-full sm:w-full"
                value={searchTerm}
                onChange={handleSearch}
              />
            </SupplierFilterField>
          </SupplierFilterToolbar>
        </AdminPageCardToolbar>

        <AdminTable
          className="shadow-none rounded-none"
          footer={
            <AdminTableFooter>
              <Pagination pagination={pagination} onPageChange={handlePageChange} />
            </AdminTableFooter>
          }
        >
          <AdminTableHead>
            <AdminTableTh>#</AdminTableTh>
            <AdminTableTh>Attraction Name</AdminTableTh>
            <AdminTableTh>Category</AdminTableTh>
            <AdminTableTh>Timings</AdminTableTh>
            <AdminTableTh>Location</AdminTableTh>
            <AdminTableTh>Status</AdminTableTh>
            <AdminTableTh>Created On</AdminTableTh>
            <AdminTableTh align="right">Actions</AdminTableTh>
          </AdminTableHead>
          <AdminTableBody>
            {attractions.length === 0 ? (
              <TableEmptyRow
                colSpan={8}
                {...getTableEmptyProps("attractions", {
                  onAdd: handleAddClick,
                })}
              />
            ) : (
              attractions.map((attraction, index) => (
                <AdminTableRow key={attraction.id}>
                  <AdminTableCell>
                    {(current_page - 1) * per_page + index + 1}
                  </AdminTableCell>
                  <AdminTableCell className="font-medium">
                    {attraction.name}
                  </AdminTableCell>
                  <AdminTableCell>
                    {Array.isArray(attraction.categories) &&
                    attraction.categories.length > 0
                      ? attraction.categories.map((c) => c.name).join(", ")
                      : attraction.attraction_category_master?.name || "N/A"}
                  </AdminTableCell>
                  <AdminTableCell nowrap className="text-xs text-gray-600">
                    {formatTimings(attraction)}
                  </AdminTableCell>
                  <AdminTableCell>{attraction.location || "N/A"}</AdminTableCell>
                  <AdminTableCell>{renderStatus(attraction)}</AdminTableCell>
                  <AdminTableCell>
                    {formatListingDateTime(attraction.created_at)}
                  </AdminTableCell>
                  <TableActionsCell>
                    <EditActionButton
                      showLabel={false}
                      onClick={() => handleEditClick(attraction.id)}
                    />
                    <DeleteActionButton
                      showLabel={false}
                      onClick={() => handleDeleteClick(attraction)}
                    />
                  </TableActionsCell>
                </AdminTableRow>
              ))
            )}
          </AdminTableBody>
        </AdminTable>
      </AdminPageCard>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title={
          <div className="flex items-center gap-2">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
            Delete Attraction
          </div>
        }
        primaryButtonText="Delete"
        primaryButtonAction={handleDeleteAttraction}
        closeButtonText="Cancel"
        closeButtonAction={() => setDeleteDialogOpen(false)}
        isLoading={isActiveLoading}
      >
        <div className="mt-2">
          <p className="text-sm text-gray-500">
            Are you sure you want to delete this attraction? This action cannot
            be undone.
          </p>
        </div>
      </Dialog>

      {successMessage && (
        <Toster
          message={successMessage}
          type="success"
          onClose={() => setSuccessMessage("")}
        />
      )}
      {errorMessage && (
        <Toster
          message={errorMessage}
          type="error"
          onClose={() => setErrorMessage("")}
        />
      )}
    </AdminPageLayout>
  );
};

export default Attractions;
